import {useEffect, useContext} from 'react';

import {useTranslation} from 'react-i18next';
import {Link, useLocation, useNavigate} from 'react-router-dom';

import {Box, Container, Grid, Grow, Typography} from '@mui/material';

import cover_image from '../../../assets/homepage.png';
import {EmpionButton} from '../../../components/EmpionButton/EmpionButton';
import {FeatureFlagContext} from '../../../contexts/FeatureFlagContext/FeatureFlagContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {TalentSurveyApiResponse} from '../../../features/talent/types';
import {useApiWrite} from '../../../hooks/useApiWrite';
import {greenColors} from '../../../layouts/theme/designSystem';
import {Post} from '../../../lib/api/POST/Post';
import {PostRoutes} from '../../../lib/api/POST/PostRoutes';
import {AppConfig} from '../../../lib/AppConfig';
import {buildUtmCodesObject} from '../../../utils/utmCodeUtils/buildUtmCodesObject';

export default function Home() {
  const {t} = useTranslation();
  const {currentUser} = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const {flags} = useContext(FeatureFlagContext);

  useEffect(() => {
    if (currentUser) {
      navigate(AppConfig.DASHBOARD_PATH);
    }
  }, [currentUser, navigate]);

  const {makeRequest} = useApiWrite<Post.CreateTalentSurvey, TalentSurveyApiResponse>({
    method: 'post',
    path: PostRoutes[Post.CreateTalentSurvey],
    onSuccess: ({talentSurvey}) => navigate(`${AppConfig.TALENT_SURVEY_WITH_ID_PATH}/${talentSurvey.resourceId}`),
  });

  const handleStartSurvey = () => {
    if (flags.CHAT_BASED_TALENT_SURVEY_ENABLED) {
      navigate(`${AppConfig.TALENT_SURVEY_WITH_ID_PATH}${location.search}`);
      return;
    }
    const utmCodes = buildUtmCodesObject(location.search);
    makeRequest({surveyType: 'short', utmCodes, startedAt: new Date().toISOString()});
  };

  return (
    <>
      <Grid
        container
        paddingTop={{sm: 0, md: 10}}
        columns={{xs: 4, sm: 6, md: 12, lg: 12}}
        spacing={{xs: 2, md: 3}}
        alignItems={'center'}
      >
        <Grid item xs={6}>
          <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
            <Container>
              <Box
                marginBottom={5}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: {xs: 'center', sm: 'center', md: 'flex-start', lg: 'flex-start'},
                  textAlign: {xs: 'center', sm: 'center', md: 'left', lg: 'left'},
                }}
              >
                <Typography
                  variant='h1'
                  gutterBottom
                  fontWeight={'400'}
                  textTransform={'uppercase'}
                  sx={{
                    fontSize: {xs: '3rem', sm: '3rem', md: '3rem', lg: '4.5rem'},
                    lineHeight: {xs: '3.5rem', sm: '3rem', md: '3rem', lg: '4.5rem'},
                  }}
                >
                  {t('home.title')}
                </Typography>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: {xs: 'block', sm: 'block', md: 'none', lg: 'none'},
                  }}
                >
                  <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
                    <Box display={'flex'} justifyContent={'flex-end'} borderRadius='16px' overflow='hidden'>
                      <img src={cover_image} alt='cover_image_small' style={{width: '100%', height: 'auto'}} />
                    </Box>
                  </Grow>
                </Grid>

                <Typography
                  variant='h4'
                  sx={{
                    fontSize: {xs: '1.2rem', sm: '1.2rem', md: '1.2rem', lg: '1.2rem'},
                    lineHeight: {xs: '2rem'},
                  }}
                >
                  {t('home.description')}
                </Typography>
              </Box>
              <Box
                display={'flex'}
                sx={{
                  justifyContent: {xs: 'center', sm: 'center', md: 'flex-start', lg: 'flex-start'},
                }}
              >
                <EmpionButton
                  size={'large'}
                  sx={{
                    p: 2,
                    fontSize: {xs: '1rem', sm: '1.2rem', md: '1.5rem', lg: '1.5rem'},
                    lineHeight: {xs: '1.5rem'},
                    width: {xs: '100%', sm: '100%', md: 'auto', lg: 'auto'},
                  }}
                  onClick={handleStartSurvey}
                >
                  {t('home.ctaButton')}
                </EmpionButton>
              </Box>
              <Box mt={3} display={'flex'} flexDirection={'row'}>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'black',
                  }}
                >
                  {t('home.alreadyHaveAnAccount')}
                </Typography>
                <Link to={AppConfig.LOGIN_PATH} style={{textDecoration: 'none'}}>
                  <Typography
                    variant='body2'
                    sx={{
                      ml: 1,
                      fontWeight: 'bold',
                      color: greenColors[900],
                      '&:hover': {
                        color: greenColors[70],
                      },
                    }}
                  >
                    {t('home.login')}
                  </Typography>
                </Link>
              </Box>
            </Container>
          </Grow>
        </Grid>
        <Grid
          item
          xs={6}
          padding={5}
          sx={{
            display: {xs: 'none', sm: 'none', md: 'block', lg: 'block'},
          }}
        >
          <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
            <Container>
              <Box display={'flex'} justifyContent={'flex-end'} borderRadius='16px' overflow='hidden'>
                <img src={cover_image} alt='cover_image_big' style={{width: '100%', height: 'auto'}} />
              </Box>
            </Container>
          </Grow>
        </Grid>
      </Grid>
    </>
  );
}
